// UploadWidget - floating panel tracking every base-image upload this session.
// Mounted at app root so it survives navigation; collapsible.

import { AlertCircle, CheckCircle2, ChevronDown, ChevronUp, Upload, X, XCircle } from 'lucide-react';
import { useEffect, useState } from 'react';
import { isInFlight, type UploadTask, useUploads } from '@/context/UploadContext';
import { cn } from '@/lib/classnames';
import { formatBytes, formatEta } from '@/lib/format';

export function UploadWidget() {
  const { tasks, cancel, dismiss, dismissCompleted } = useUploads();
  const [collapsed, setCollapsed] = useState(false);

  // Auto-expand whenever a new upload starts so the user sees it.
  const uploadingCount = tasks.filter((t) => isInFlight(t.status)).length;
  useEffect(() => {
    if (uploadingCount > 0) setCollapsed(false);
  }, [uploadingCount]);

  if (tasks.length === 0) return null;

  const hasFinished = tasks.some((t) => !isInFlight(t.status));

  return (
    <div className="fixed bottom-4 right-4 z-50 w-[360px] max-w-[calc(100vw-2rem)]">
      <div className="bg-ink-900 border border-ink-800 rounded-lg shadow-xl overflow-hidden">
        <button
          className="w-full flex items-center justify-between px-3 py-2 bg-ink-900/80 hover:bg-ink-800/60 border-b border-ink-800/60 ring-focus"
          onClick={() => setCollapsed((c) => !c)}
        >
          <div className="flex items-center gap-2 text-sm text-ink-200">
            <Upload className="w-4 h-4 text-ink-400" />
            <span>
              {uploadingCount > 0
                ? `Uploading ${uploadingCount} image${uploadingCount > 1 ? 's' : ''}`
                : `${tasks.length} upload${tasks.length > 1 ? 's' : ''} done`}
            </span>
          </div>
          <div className="flex items-center gap-2">
            {hasFinished && (
              <span
                className="text-[11px] text-ink-500 hover:text-ink-200 px-1.5 py-0.5"
                onClick={(e) => {
                  e.stopPropagation();
                  dismissCompleted();
                }}
                role="button"
              >
                Clear
              </span>
            )}
            {collapsed ? (
              <ChevronUp className="w-4 h-4 text-ink-500" />
            ) : (
              <ChevronDown className="w-4 h-4 text-ink-500" />
            )}
          </div>
        </button>

        {!collapsed && (
          <div className="max-h-[40vh] overflow-y-auto divide-y divide-ink-800/60">
            {tasks.map((t) => (
              <UploadRow key={t.id} task={t} onCancel={() => cancel(t.id)} onDismiss={() => dismiss(t.id)} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function UploadRow({
  task,
  onCancel,
  onDismiss,
}: {
  task: UploadTask;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  const pct = task.size > 0 ? Math.min(100, (task.loaded / task.size) * 100) : 0;
  const elapsed = (task.endedAt ?? Date.now()) - task.startedAt;
  const speedBps = elapsed > 0 ? (task.loaded / elapsed) * 1000 : 0;
  const remainingBytes = Math.max(0, task.size - task.loaded);
  const etaSec = speedBps > 0 ? remainingBytes / speedBps : 0;

  return (
    <div className="px-3 py-2.5 bg-ink-900/40">
      <div className="flex items-start gap-2">
        <StatusIcon status={task.status} />
        <div className="flex-1 min-w-0">
          <div className="flex items-baseline gap-2 min-w-0">
            <span className="text-sm text-ink-100 font-medium truncate">{task.imageName}</span>
            <span className="text-[11px] text-ink-500 tabular-nums shrink-0">
              {formatBytes(task.size)}
            </span>
          </div>
          <div className="text-[11px] text-ink-500 truncate">{task.fileName}</div>

          {isInFlight(task.status) && (
            <>
              <div className="mt-1.5 h-1 bg-ink-800 rounded overflow-hidden">
                <div
                  className="h-full bg-accent-500 transition-[width] duration-200"
                  style={{ width: `${pct}%` }}
                />
              </div>
              <div className="mt-1 flex items-center justify-between text-[11px] text-ink-500 tabular-nums">
                <span>
                  {phaseLabel(task.status)} · {formatBytes(task.loaded)} / {formatBytes(task.size)} · {pct.toFixed(1)}%
                </span>
                <span>{speedBps > 0 ? `${formatBytes(speedBps)}/s · ${formatEta(etaSec)}` : '-'}</span>
              </div>
            </>
          )}
          {task.status === 'error' && (
            <div className="mt-1 text-[11px] text-rose-400 break-words">{task.error}</div>
          )}
          {task.status === 'success' && task.digest && (
            <div className="mt-1 text-[11px] font-mono text-ink-500 truncate">
              sha256:{task.digest.slice(0, 12)}…
            </div>
          )}
        </div>
        <RowAction task={task} onCancel={onCancel} onDismiss={onDismiss} />
      </div>
    </div>
  );
}

function StatusIcon({ status }: { status: UploadTask['status'] }) {
  if (status === 'success') return <CheckCircle2 className="w-4 h-4 text-verdict-clean shrink-0 mt-0.5" />;
  if (status === 'error') return <AlertCircle className="w-4 h-4 text-verdict-malicious shrink-0 mt-0.5" />;
  if (status === 'canceled') return <XCircle className="w-4 h-4 text-ink-500 shrink-0 mt-0.5" />;
  return (
    <div className="w-4 h-4 shrink-0 mt-0.5 flex items-center justify-center">
      <div className="w-3 h-3 rounded-full border-2 border-accent-500 border-t-transparent animate-spin" />
    </div>
  );
}

function phaseLabel(status: UploadTask['status']): string {
  switch (status) {
    case 'hashing':
      return 'hashing';
    case 'uploading':
      return 'uploading';
    case 'committing':
      return 'committing';
    default:
      return status;
  }
}

function RowAction({
  task,
  onCancel,
  onDismiss,
}: {
  task: UploadTask;
  onCancel: () => void;
  onDismiss: () => void;
}) {
  if (isInFlight(task.status)) {
    return (
      <button
        className="btn-ghost btn-sm text-ink-500 hover:text-rose-400 -mr-1"
        onClick={onCancel}
        title="Cancel upload"
        aria-label="Cancel upload"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    );
  }
  return (
    <button
      className={cn('btn-ghost btn-sm -mr-1', 'text-ink-500 hover:text-ink-200')}
      onClick={onDismiss}
      title="Dismiss"
      aria-label="Dismiss"
    >
      <X className="w-3.5 h-3.5" />
    </button>
  );
}

